import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { mediaUrl } from "@/lib/api";

function storageKey(tenantId) {
  return `ms_adult_ok:${tenantId || "store"}`;
}

export default function StorefrontAgeGate({ config }) {
  const ac = config?.adult_control || {};
  const tenantId = config?.tenant?.id || config?.tenant?.slug || "";
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    if (!ac.enabled) {
      setOpen(false);
      setBlocked(false);
      setReady(true);
      return undefined;
    }
    let ok = false;
    try {
      ok = window.localStorage.getItem(storageKey(tenantId)) === "1";
    } catch { /* ignore */ }
    setOpen(!ok);
    setBlocked(false);
    setReady(true);
    return undefined;
  }, [ac.enabled, tenantId]);

  useEffect(() => {
    if (!open && !blocked) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open, blocked]);

  if (!ac.enabled || !ready || (!open && !blocked)) return null;
  if (typeof document === "undefined") return null;

  const bg = ac.background_image_url ? mediaUrl(ac.background_image_url) : "";
  const restrictedLines = String(ac.restricted_body || "").split(/\n+/).filter(Boolean);

  const enter = () => {
    try { window.localStorage.setItem(storageKey(tenantId), "1"); } catch { /* ignore */ }
    setBlocked(false);
    setOpen(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-testid="adult-gate-overlay" role="dialog" aria-modal="true" aria-label="Age verification">
      <style>{`
        @import url("https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&display=swap");
        .adult-gate-title { font-family: "Playfair Display", Georgia, serif; }
        .adult-gate-glow { box-shadow: 0 0 18px rgba(255, 0, 0, 0.55), 0 0 36px rgba(255, 0, 0, 0.22); }
        .adult-gate-btn { box-shadow: 0 0 18px rgba(255, 0, 0, 0.55); }
      `}</style>
      {bg ? (
        <div className="absolute inset-0 bg-black" aria-hidden>
          <img src={bg} alt="" className="h-full w-full object-cover blur-md scale-110 opacity-70" />
          <div className="absolute inset-0 bg-black/55" />
        </div>
      ) : (
        <div className="absolute inset-0 bg-[#070707]" aria-hidden />
      )}
      <div className="relative w-full max-w-[560px] rounded-[22px] border border-white/10 bg-[#121212] px-6 py-8 sm:px-10 sm:py-10 text-center text-white shadow-[0_24px_80px_rgba(0,0,0,.55)]">
        {blocked ? (
          <div data-testid="adult-gate-restricted">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border-2 border-red-600 adult-gate-glow text-lg font-bold">{ac.badge || "18+"}</div>
            <h1 className="adult-gate-title text-3xl sm:text-[2.1rem] font-semibold">{ac.restricted_title || "Access Restricted"}</h1>
            <div className="mx-auto mt-3 h-px w-16 bg-red-600" />
            <div className="mt-6 space-y-4 text-sm sm:text-base text-white/90 leading-relaxed">
              {restrictedLines.map((line, i) => <p key={i}>{line}</p>)}
            </div>
          </div>
        ) : (
          <>
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full border-2 border-red-600 adult-gate-glow text-lg font-bold tracking-wide" data-testid="adult-gate-badge">{ac.badge || "18+"}</div>
            <h1 className="adult-gate-title text-3xl sm:text-[2.15rem] font-semibold leading-tight" data-testid="adult-gate-title">{ac.title}</h1>
            <p className="mt-3 text-[11px] sm:text-xs tracking-[0.18em] uppercase text-white/55 font-medium">{ac.subtitle}</p>
            <div className="mx-auto mt-3 h-px w-16 bg-red-600" />
            <p className="mt-6 text-sm sm:text-[15px] leading-relaxed text-white/90" data-testid="adult-gate-body">{ac.body}</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button type="button" onClick={enter} data-testid="adult-gate-enter" className="adult-gate-btn rounded-lg bg-[#e10600] hover:bg-[#c10500] px-4 py-3.5 text-white">
                <span className="block text-sm font-extrabold tracking-wide">{ac.enter_label}</span>
                <span className="block mt-1 text-[10px] font-semibold tracking-wider text-white/85">{ac.enter_sub}</span>
              </button>
              <button type="button" onClick={() => setBlocked(true)} data-testid="adult-gate-leave" className="rounded-lg border border-white/25 bg-transparent px-4 py-3.5 text-white hover:bg-white/5">
                <span className="block text-sm font-extrabold tracking-wide">{ac.leave_label}</span>
                <span className="block mt-1 text-[10px] font-semibold tracking-wider text-white/50">{ac.leave_sub}</span>
              </button>
            </div>
            <div className="mt-7 flex items-center gap-3 text-[10px] tracking-[0.16em] uppercase text-white/40">
              <span className="h-px flex-1 bg-white/15" />
              <span>{ac.footer}</span>
              <span className="h-px flex-1 bg-white/15" />
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
